import { apiClient } from '@/lib/api/api-client';
import type { ImportResult } from '@/features/master-data/types/master-data.types';

/**
 * Catálogo arancelario NANDINA — bloque B de Trade Intelligence.
 * Diseño: vaultProgress/80 · Sesión: vaultProgress/86
 *
 * Dos capas conviven acá:
 *  - El catálogo OFICIAL es global (lo mantiene Datec, es ley nacional). Solo
 *    lectura, salvo el import y la activación de versiones, que son PLATFORM_ADMIN.
 *  - Las aperturas y los sinónimos son DEL TENANT y sí se editan.
 */
const BASE = '/api/master-data';

/** De dónde salió un resultado de búsqueda. */
export type OrigenResultado = 'OFICIAL' | 'SINONIMO' | 'APERTURA';

export const ORIGEN_LABELS: Record<OrigenResultado, string> = {
  OFICIAL: 'Arancel oficial',
  SINONIMO: 'Encontrado por tu sinónimo',
  APERTURA: 'Tu apertura interna',
};

export interface TariffHeading {
  id: string;
  code: string;
  description: string;
  section: string | null;
  chapter: string;
  chapterName: string | null;
  heading: string | null;
  /** Fracción: 0.05 = 5% */
  gaRate: string | number;
  ivaRate: string | number;
  isIvaExempt: boolean;
  /** `null` = NO TENEMOS EL DATO. No es "está exento". */
  hasIce: boolean | null;
  hasIehd: boolean | null;
  unitOfMeasure: string | null;
  /** Legado del dataset del HTML — el arancel 2026 usa las columnas pref*. */
  canPreference: string | null;
  /**
   * Preferencias arancelarias por acuerdo, tal como las publica el arancel:
   * % de rebaja SOBRE EL GA (100 = liberado). Texto porque el protocolo del
   * ACE 22 sale como "VII"/"II". `null` = la celda del libro está vacía
   * (= sin rebaja publicada) SI la versión trae las columnas — ver
   * `traePreferencias` en el detalle.
   */
  prefCanAce36Ace47?: string | null;
  prefAce22Chi?: string | null;
  prefAce22Prot?: string | null;
  prefAce66Mex?: string | null;
  /**
   * La mercadería NO puede importarse a Bolivia. Son 76 partidas y no hay
   * trámite que las habilite.
   *
   * `null` = NO TENEMOS EL DATO (las versiones viejas del arancel no lo traían).
   * No es "se puede importar" — mismo criterio que `hasIce`.
   */
  importProhibited?: boolean | null;
  /** Norma que exige el documento o que prohíbe la importación. */
  dispatchDocLegalBasis?: string | null;
  dispatchDocType?: string | null;
  dispatchDocEntity?: string | null;
  /**
   * Cadena de padres publicada ("Partida > rama > ... > descripción propia").
   * Es lo que hace legible un resultado que solo dice "Las demás".
   */
  fullContext?: string | null;
  parentCodes?: string | null;
}

export interface ResultadoBusqueda extends TariffHeading {
  origen: OrigenResultado;
  /**
   * La partida ES la cosa buscada, no solo habla de ella: su texto raíz empieza
   * por el sustantivo núcleo. Separa el calzado del betún PARA calzado.
   *
   * `undefined` = no se midió (la partida entró por coincidencia textual, no
   * por el consenso). No es `false`.
   */
  esLaCosa?: boolean;
  /**
   * El texto NIEGA alguno de los términos pedidos ("sin puntera metálica").
   * `undefined` = no se midió, igual que `esLaCosa`.
   */
  niega?: boolean;
  /** Presente si origen === 'SINONIMO' */
  viaTermino?: string;
  /** Presentes si origen === 'APERTURA' */
  aperturaId?: string;
  codigoInterno?: string;
  descripcionInterna?: string;
  codigoOficialPadre?: string;
}

/**
 * Cómo se resolvió la consulta (vault 96 §2). La UI lo usa para explicar el
 * resultado, no para decidir lógica de negocio:
 *  - CODIGO: lookup directo, sin IA.
 *  - TEXTO_SIMPLE: búsqueda textual de Postgres, sin IA.
 *  - IA: la descripción pasó por el intérprete ANTES de buscar.
 *  - OR_FALLBACK: el AND dio 0 y cayó a la red de seguridad → "aproximados".
 */
export type CaminoBusqueda = 'CODIGO' | 'TEXTO_SIMPLE' | 'IA' | 'OR_FALLBACK';

/**
 * Qué entendió la IA intérprete. Se muestra COMPLETO: el usuario tiene que
 * poder auditar la traducción (qué buscó y qué ignoró), nunca confiar a ciegas.
 * Los candidatos siempre salen de la base — la IA solo traduce vocabulario.
 */
export interface InterpretacionBusqueda {
  /** Qué ES el producto según la IA ("camioneta de carga con motor diesel"). */
  concepto: string;
  /** Términos del vocabulario legal con los que se buscó en la base. */
  terminosLegales: string[];
  /** Tokens ignorados (marca, modelo, medidas) — no cambian la partida. */
  descartado: string[];
  /**
   * Qué palabra del usuario se tradujo a qué vocabulario del arancel. Sin esto,
   * buscar "que cubran el tobillo" cuando el usuario escribió "botas" parece
   * que el buscador ignoró lo que pidió.
   *
   * Opcional: cuando no hubo nada que traducir el modelo no devuelve el campo.
   */
  traducciones?: Array<{ de: string; a: string }>;
}

/**
 * Cuántas partidas encuentra cada término con el que se buscó.
 *
 * `inexistente` NO es `partidas === 0`: en el backend es
 * `partidas === 0 || enElTexto === 0`, así que un término puede traer partidas
 * por coincidencia suelta y aun así no aparecer nunca como frase en el libro.
 * Es la señal de que se descartó del puntaje.
 */
export interface ConteoTerminoBusqueda {
  termino: string;
  partidas: number;
  inexistente: boolean;
}

/**
 * Capítulo sugerido porque una NOTA LEGAL menciona el término buscado
 * ("ropa" → nota del cap. 63). Se muestra a nivel capítulo con la cita,
 * nunca como 60 partidas idénticas.
 */
export interface CapituloSugerido {
  chapter: string | null;
  chapterName: string | null;
  scope: string;
  noteType: string;
  heading: string | null;
  /** Cita de por qué matcheó, con <b></b> de resaltado (ts_headline). */
  fragmento: string;
  totalPartidasDelCapitulo: number;
}

/** ¿Le sirvió la búsqueda al usuario? (tarea D1.) */
export type Calificacion = 'UTIL' | 'NO_UTIL';

export interface RespuestaBusqueda {
  resultados: ResultadoBusqueda[];
  version?: { id: string; nombre: string };
  /**
   * El id de ESTA búsqueda en el historial, para poder calificarla. `null`
   * cuando no hubo consulta: navegar el árbol no es una búsqueda.
   */
  busquedaId?: string | null;
  matchPorCodigo?: boolean;
  sinResultados?: boolean;
  sinCatalogo?: boolean;
  mensaje?: string;
  /** true = el AND exacto dio 0 y los resultados son aproximados (OR). */
  matchParcial?: boolean;
  /**
   * true = se intentó usar el intérprete IA y NO estaba disponible (key
   * rechazada, timeout, red, o apagado por configuración). Distinto de que la
   * consulta no necesitara IA: en ese caso viene false.
   */
  interpreteNoDisponible?: boolean;
  capitulosSugeridos?: CapituloSugerido[];
  camino?: CaminoBusqueda;
  interpretacion?: InterpretacionBusqueda | null;
  /** 2–5 candidatos EMPATADOS en el consenso máximo → botones "¿cuál describe mejor su producto?". */
  requiereClarificacion?: boolean;
  /** Cuántos son: los PRIMEROS N de `resultados` (el ranking ya los puso adelante). */
  candidatosClarificacion?: number;
  /**
   * Cuántas partidas encontró cada término. Vacío cuando el camino no contó
   * (código o texto simple) — ahí "no sé" no se dibuja como cero.
   */
  conteos?: ConteoTerminoBusqueda[];
  /**
   * Las frases del arancel que la segunda pasada eligió para desempatar. Son
   * texto del libro, no de la IA: si aparecen, el orden se decidió con ellas.
   */
  ramasDelCapitulo?: string[];
  /**
   * La IA no reconoció el producto y salió a internet a averiguar qué era. Sin
   * esto una respuesta apoyada en internet se ve igual que una que salió del
   * arancel, y el usuario no puede pesarlas distinto.
   */
  usoInternet?: boolean;
}

export interface CapituloArbol {
  section: string | null;
  chapter: string;
  chapterName: string | null;
  partidas: number;
}

export interface RespuestaArbol {
  secciones: Array<{ section: string; capitulos: CapituloArbol[] }>;
  version?: { id: string; nombre: string };
  capitulosCargados?: number;
  capitulosDelArancel?: number;
  advertencia?: string;
  sinCatalogo?: boolean;
}

/**
 * Nota legal que alcanza a una partida (las de su sección + las de su
 * capítulo). Son texto de ley: definen qué entra y qué no, y mandan por
 * encima de la descripción de la partida.
 */
/**
 * Un nodo del capítulo con la forma del libro (tarea A3).
 *
 * Los `nivel` son los guiones del arancel (`-`, `--`, `---`): son TÍTULOS y no
 * llevan alícuota. Solo las partidas de verdad la tienen. Sin esta distinción,
 * "Los demás" aparece cuatro veces seguidas sin decir demás de qué (vault 126 §3.1).
 */
export interface NodoArancel {
  tipo: 'nivel' | 'partida';
  texto: string;
  /** 0 = partida de 4 dígitos. 1, 2, 3… = cantidad de guiones en el libro. */
  nivel: number;
  /** Código, cuando el nodo lo tiene. Las hojas siempre; los niveles, a veces. */
  code: string | null;
  /** En PUNTOS porcentuales (15 = 15 %) — el backend ya convirtió la fracción. */
  gaRate?: number;
  ivaRate?: number;
  hijos: NodoArancel[];
}

export interface RespuestaArbolCapitulo {
  capitulo: string;
  nombreCapitulo: string;
  arbol: NodoArancel[];
  version?: { id: string; nombre: string };
  sinCatalogo?: boolean;
}

export interface NotaLegalPartida {
  scope: string; // 'SECCION' | 'CAPITULO'
  noteType: string;
  heading: string | null;
  text: string;
  sourcePage: number | null;
}

/**
 * Tasa ICE de vehículos por antigüedad (Apéndice I del arancel).
 * `rate` es fracción (0.15 = 15 %), igual que gaRate.
 */
export interface TasaIceAntiguedad {
  yearLabel: string; // 'NUEVOS' | '1'..'5'
  rate: string | number;
  importProhibited: boolean;
}

export interface DetallePartida extends TariffHeading {
  versionId: string;
  aperturas: TenantSubheading[];
  sinonimos: TariffSynonym[];
  notas: NotaLegalPartida[];
  /** Título de la sección ("Material de transporte"). null en versiones sin secciones cargadas. */
  sectionTitle: string | null;
  /** Partida de 4 dígitos con su texto real del arancel ("87.04" + "Vehículos automóviles para..."). */
  partidaNivel: { code: string; text: string } | null;
  /**
   * Ramas intermedias entre la partida y el código final, en el orden
   * publicado. Pueden no tener código propio (agrupadores sin numerar) —
   * por eso son textos, no pares código/texto.
   */
  ramasIntermedias: string[];
  /**
   * Tasas ICE por antigüedad. Lista vacía con hasIce=true significa "paga
   * pero la tasa no está cargada" (59 códigos del PDF pendientes) — decirlo,
   * nunca callarlo.
   */
  iceRates: TasaIceAntiguedad[];
  version: { id: string; nombre: string; norma: string | null };
  /** Texto a mostrar cuando no sabemos si paga ICE. */
  avisoIce: string | null;
  /**
   * Texto a mostrar cuando la mercadería no puede importarse. Se lee ANTES que
   * cualquier otra cosa de la ficha: si no entra al país, el resto es contexto.
   */
  avisoProhibicion: string | null;
  /**
   * Permiso previo exigido para despachar. El backend lo deja en `null` cuando la
   * partida está prohibida — ahí no hay documento que conseguir — y cuando la
   * marca es "( * )", que no es un documento (ver `avisoRegimenVehiculos`).
   */
  avisoDocumento: string | null;
  /**
   * La marca "( * )" del capítulo 87: remisión al régimen de importación de
   * vehículos (D.S. 28963, Normas Euro, D.S. 5142). Texto fiel a la leyenda
   * de la pág. 475 del arancel oficial.
   */
  avisoRegimenVehiculos: string | null;
  /**
   * Columna "Despacho Frontera" del arancel: la norma que obliga a despachar
   * esta mercancía en frontera ("RM 016" / "RM 139"). Tal cual el libro —
   * no trae leyenda que la expanda. 696 partidas la llevan.
   */
  borderDispatch?: string | null;
  /**
   * true = esta versión del catálogo trae las columnas de preferencias, así
   * que una celda vacía significa "sin rebaja publicada". false = dataset
   * viejo sin las columnas: vacío es "sin dato", no "sin rebaja".
   */
  traePreferencias: boolean;
}

export interface TenantSubheading {
  id: string;
  tenantId: string;
  tariffHeadingId: string;
  internalCode: string;
  description: string;
  notes: string | null;
  isActive: boolean;
  tariffHeading?: {
    code: string;
    description: string;
    gaRate: string | number;
    ivaRate: string | number;
    isIvaExempt: boolean;
  };
}

export interface TariffSynonym {
  id: string;
  tenantId: string;
  tariffHeadingId: string;
  term: string;
  tariffHeading?: { code: string; description: string };
}

export interface TariffVersion {
  id: string;
  countryCode: string;
  name: string;
  legalBasis: string | null;
  validFrom: string;
  validTo: string | null;
  isActive: boolean;
  notes: string | null;
  _count?: { headings: number };
}

/**
 * 7D-1: el resultado de una importación del arancel EXTIENDE el del diálogo
 * único de importación (`ImportResult`): así `avisos` (los del decodificador de
 * CSV) viaja en el mismo tipo y ningún `importFn` necesita rearmar el objeto
 * (que era donde se perdían). `updated` queda opcional porque la importación
 * de versiones del arancel no lo manda.
 */
export interface ResultadoImport extends Omit<ImportResult, 'updated'> {
  updated?: number;
  version?: { id: string; nombre: string; activa: boolean };
  capitulos?: Array<{ chapter: string; partidas: number }>;
  advertencias?: string[];
}

/** Convierte la fracción guardada (0.05) al porcentaje que se muestra (5%). */
export function aPorcentaje(fraccion: string | number): number {
  return Number(fraccion) * 100;
}

// ── Simulador de tributos (vault 101 §3) ──
// El backend reusa el motor real de liquidación (customs-duty.service): los
// números y las citas legales son los mismos del tracking de importaciones.

export interface MontosLiquidacion {
  fob: number;
  flete: number;
  seguro: number;
  cif: number;
  ga: number;
  baseIva: number;
  iva: number;
  totalTributos: number;
  costoNacionalizado: number;
}

export interface AvisoLiquidacion {
  nivel: 'PROHIBICION' | 'ADVERTENCIA' | string;
  codigo: string;
  mensaje: string;
  norma?: string;
}

export interface SimulacionTributos {
  partida: { code: string; description: string };
  gaRate: number;
  ivaRateEfectivo: number;
  exentaIva: boolean;
  usd: MontosLiquidacion;
  /** null cuando no hay TC (ni manual ni cargado): la UI lo dice, no lo calla. */
  bs: MontosLiquidacion | null;
  tcUsado: number | null;
  /** Último TC cargado del tenant, con fecha y fuente — el usuario decide si le sirve. */
  tcSugerido: { valor: number; fecha: string; fuente: string; desactualizado: boolean } | null;
  /** Los pasos del motor real, en USD, con la cita legal de cada uno. */
  pasos: string[];
  avisos: AvisoLiquidacion[];
}

export interface EntradaSimulacion {
  fob: number;
  flete?: number;
  seguro?: number;
  moneda?: 'USD' | 'BOB';
  tc?: number;
  via?: 'MARITIMA' | 'AEREA' | 'TERRESTRE';
}

export interface FilaHistorial {
  id: string;
  createdAt: string;
  userId: string | null;
  consulta: string;
  camino: CaminoBusqueda;
  cacheHit: boolean;
  costUsd: number;
  latencyMs: number;
  resultados: number;
  /** El filtro de capítulo puesto al buscar, si había. Un `resultados: 0` con
   *  filtro NO es una búsqueda fallida — la pantalla lo tiene que decir. */
  filtroCapitulo: string | null;
  filtroIce: boolean;
  interpretacion: {
    concepto: string;
    terminosLegales: string[];
    descartado: string[];
  } | null;
}

export interface ResumenHistorial {
  consultas: number;
  gastoUsd: number;
  cacheHits: number;
  /** Consultas que pasaron por el camino IA — el DENOMINADOR de `cachePorcentaje`.
   *  No es el total: una búsqueda por código nunca pudo pegarle al cache. */
  consultasIa: number;
  cachePorcentaje: number;
  costoPromedioIa: number;
  /** ESTIMADO: hits × costo promedio del período. Se rotula como tal en la UI. */
  ahorroEstimadoUsd: number;
}

export interface RespuestaHistorial {
  filas: FilaHistorial[];
  total: number;
  page: number;
  pageSize: number;
  resumen: ResumenHistorial;
}

export const tariffCatalogService = {
  buscar: (params: {
    q?: string;
    chapter?: string;
    hasIce?: boolean;
    at?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.chapter) qs.set('chapter', params.chapter);
    if (params.hasIce) qs.set('hasIce', 'true');
    if (params.at) qs.set('at', params.at);
    if (params.limit) qs.set('limit', String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return apiClient.get<RespuestaBusqueda>(`${BASE}/tariff-headings${tail}`);
  },

  arbol: (at?: string) =>
    apiClient.get<RespuestaArbol>(`${BASE}/tariff-tree${at ? `?at=${at}` : ''}`),

  /**
   * ¿Le sirvió la búsqueda? (tarea D1.) Cada 'NO_UTIL' con su código correcto se
   * vuelve un caso nuevo del set de control.
   */
  calificarBusqueda: (
    id: string,
    dto: { calificacion: Calificacion; comentario?: string; codigoCorrecto?: string }
  ) =>
    apiClient.post<{ calificacion: Calificacion }>(
      `${BASE}/tariff-searches/${id}/calificacion`,
      dto
    ),

  /**
   * En qué partida terminó el usuario. Se manda solo, al abrir una ficha desde
   * los resultados — no se le pregunta nada.
   */
  registrarPartidaElegida: (id: string, codigoElegido: string) =>
    apiClient.post(`${BASE}/tariff-searches/${id}/elegida`, { codigoElegido }),

  /** Un capítulo entero con la estructura de niveles del libro (tarea A3). */
  arbolCapitulo: (capitulo: string, at?: string) =>
    apiClient.get<RespuestaArbolCapitulo>(
      `${BASE}/tariff-chapters/${capitulo}/tree${at ? `?at=${at}` : ''}`
    ),

  detalle: (code: string, at?: string) =>
    apiClient.get<DetallePartida>(`${BASE}/tariff-headings/${code}${at ? `?at=${at}` : ''}`),

  simular: (code: string, dto: EntradaSimulacion) =>
    apiClient.post<SimulacionTributos>(`${BASE}/tariff-headings/${code}/simulacion`, dto),

  // ── Versiones (capa global — el import y la activación son PLATFORM_ADMIN) ──
  versiones: () => apiClient.get<TariffVersion[]>(`${BASE}/tariff-versions`),

  activarVersion: (id: string, isActive: boolean) =>
    apiClient.patch<TariffVersion>(`${BASE}/tariff-versions/${id}`, { isActive }),

  importarArancel: (
    file: File,
    datos: { nombre: string; norma?: string; vigenciaDesde: string; vigenciaHasta?: string }
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('nombre', datos.nombre);
    fd.append('vigencia_desde', datos.vigenciaDesde);
    if (datos.norma) fd.append('norma', datos.norma);
    if (datos.vigenciaHasta) fd.append('vigencia_hasta', datos.vigenciaHasta);
    return apiClient.post<ResultadoImport>(`${BASE}/tariff-versions/import`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // ── Aperturas internas (del tenant) ──
  aperturas: () => apiClient.get<TenantSubheading[]>(`${BASE}/tariff-subheadings`),

  crearApertura: (dto: {
    tariffHeadingId: string;
    internalCode: string;
    description: string;
    notes?: string | null;
  }) => apiClient.post<TenantSubheading>(`${BASE}/tariff-subheadings`, dto),

  actualizarApertura: (
    id: string,
    dto: Partial<{
      tariffHeadingId: string;
      internalCode: string;
      description: string;
      notes: string | null;
    }>
  ) => apiClient.patch<TenantSubheading>(`${BASE}/tariff-subheadings/${id}`, dto),

  eliminarApertura: (id: string) => apiClient.delete<void>(`${BASE}/tariff-subheadings/${id}`),

  importarAperturas: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiClient.post<ImportResult>(`${BASE}/tariff-subheadings/import`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // ── Sinónimos (del tenant) ──
  /** Historial de búsquedas + resumen de gasto. Solo administradores (el
   *  backend responde 403 al resto). */
  historial: (params: {
    desde?: string;
    hasta?: string;
    camino?: CaminoBusqueda;
    soloConCosto?: boolean;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.desde) qs.set('desde', params.desde);
    if (params.hasta) qs.set('hasta', params.hasta);
    if (params.camino) qs.set('camino', params.camino);
    if (params.soloConCosto) qs.set('soloConCosto', 'true');
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return apiClient.get<RespuestaHistorial>(`${BASE}/tariff-searches${tail}`);
  },

  sinonimos: () => apiClient.get<TariffSynonym[]>(`${BASE}/tariff-synonyms`),

  crearSinonimo: (dto: { tariffHeadingId: string; term: string }) =>
    apiClient.post<TariffSynonym>(`${BASE}/tariff-synonyms`, dto),

  eliminarSinonimo: (id: string) => apiClient.delete<void>(`${BASE}/tariff-synonyms/${id}`),
};
